import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useUpdateEffect from 'react/shared/hooks/UseUpdateEffect';
import apiMapper from 'react/signups/card/ApiMapper';
import shippingMapper from 'react/signups/card/ShippingMapper';
import api from 'react/signups/card/api';
import ClientShipping from 'react/signups/card/modules/shipping/client/Shipping';
import FamilyShipping from 'react/signups/card/modules/shipping/family/Shipping';
import { USER_TYPES } from 'react/signups/card/recipes/constants';
import { goToNextStep, overrideNextModule } from 'react/signups/card/routing/navigator';
import cardAccountSignupShape from 'react/signups/card/shapes/CardAccountSignup';
import organizationShape from 'react/signups/card/shapes/Organization';
import recipeShape from 'react/signups/card/shapes/Recipe';

export default function Shipping({ account, cardAccountSignup, organization, recipe, setAccount }) {
  const location = useLocation();
  const navigate = useNavigate();
  const userType = cardAccountSignup.userType;

  // -----------------------------------------------------------------------------------------------

  // Arbiters of initial state ----------

  // State hooks -----------------------------------------------------------------------------------

  // Module-level navigation state
  const [moduleComplete, setModuleComplete] = useState(false);

  // Module-level unpersisted form state

  const [shippingData, setShippingData] = useState({
    shippingDestination: account.shippingDestination || '',
    otherAddress: {
      street1: account.shippingAddress?.street1 || '',
      street2: account.shippingAddress?.street2 || '',
      city: account.shippingAddress?.city || '',
      state: account.shippingAddress?.state || '',
      zip: account.shippingAddress?.zip || '',
    },
  });

  // -----------------------------------------------------------------------------------------------

  // Effect hooks and memoized callbacks -----------------------------------------------------------

  // Can delete this when trust_compliance_questionnaire is deleted
  const buildRecipe = useCallback(() => {
    let nextModule = recipe.variants[userType].next;

    if (!$tlf?.flipperEnableTrustComplianceQuestionnaire) {
      if (userType === USER_TYPES.FAMILY) {
        nextModule = recipe.variants[userType].alternativeNext.complianceDisabled;
      }
    }

    const foo = overrideNextModule({
      nextModule,
      recipe,
      userType,
    });
    return foo;
  }, [recipe, userType]);

  const localRecipe = useMemo(() => buildRecipe(), [buildRecipe]);

  // Move to next step or mark module complete
  const onNavigate = useCallback(() => {
    goToNextStep({
      cardAccountSignupId: cardAccountSignup.id,
      location,
      navigate,
      moduleComplete,
      setModuleComplete,
      recipe: localRecipe,
      userType,
    });
  }, [
    location,
    navigate,
    localRecipe,
    userType,
    cardAccountSignup,
    moduleComplete,
    setModuleComplete,
  ]);

  // Send module payload and update Application-level state
  useUpdateEffect(() => {
    if (!moduleComplete) return;

    const shippingDestination = shippingData.shippingDestination;
    const shippingAddress = shippingMapper.getShippingAddress({
      cardholderAddress: account.cardholder?.address,
      organizationAddress: organization.address,
      shippingDestination,
      otherAddress: shippingData.otherAddress,
    });

    api
      .updateAccount(
        account.id,
        apiMapper.buildShippingAccountParams({
          shippingAddress,
          shippingDestination,
        }),
      )
      .then((response) => {
        setAccount(apiMapper.mapServerAccountToClientAccount(response.data.data));
      });
  }, [moduleComplete]);

  // Navigate to next step when Module-level state changes in response to form submit or api success
  useUpdateEffect(() => {
    onNavigate();
  }, [shippingData, account]);

  // -----------------------------------------------------------------------------------------------

  // Pass-down state, props and handlers -----------------------------------------------------------

  const passDownValues = {
    cardAccountSignup,
    cardholderAddress: account.cardholder?.address,
    firstName: cardAccountSignup.cardholderFirstName || account.cardholder?.firstName,
    moduleComplete,
    organizationAddress: organization.address,
    shippingData,
  };

  const passDownHandlers = {
    setShippingData,
  };

  return (
    <>
      {userType === USER_TYPES.CLIENT && (
        <ClientShipping {...passDownValues} {...passDownHandlers} />
      )}
      {userType === USER_TYPES.FAMILY && (
        <FamilyShipping {...passDownValues} {...passDownHandlers} />
      )}
    </>
  );
}

Shipping.propTypes = {
  account: PropTypes.object.isRequired,
  cardAccountSignup: cardAccountSignupShape.isRequired,
  organization: organizationShape.isRequired,
  recipe: recipeShape.isRequired,
  setAccount: PropTypes.func.isRequired,
};
